import { useEffect, useRef, useState, type ImgHTMLAttributes } from "react";
import { responsiveImage } from "@/lib/responsive-images";

type Props = ImgHTMLAttributes<HTMLImageElement> & {
  /** Mark as above-the-fold; loads eagerly with high priority. */
  priority?: boolean;
};

/**
 * Drop-in <img> replacement:
 *  - lazy-loads below the fold
 *  - decodes async
 *  - fades in on load (cache hits also fade smoothly)
 */
export function SmartImage({
  priority = false,
  className = "",
  loading,
  decoding,
  fetchPriority,
  onLoad,
  src,
  srcSet,
  sizes,
  width,
  height,
  ...rest
}: Props) {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  const responsive = typeof src === "string" ? responsiveImage(src) : undefined;

  useEffect(() => {
    // If image is already cached/complete on mount, mark loaded immediately.
    if (ref.current?.complete && ref.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);

  return (
    <img
      ref={ref}
      src={src}
      srcSet={srcSet ?? responsive?.srcSet}
      sizes={sizes ?? (responsive ? "(max-width: 767px) 100vw, 50vw" : undefined)}
      width={width ?? responsive?.width}
      height={height ?? responsive?.height}
      loading={loading ?? (priority ? "eager" : "lazy")}
      decoding={decoding ?? "async"}
      fetchPriority={fetchPriority ?? (priority ? "high" : "auto")}
      onLoad={(e) => {
        setLoaded(true);
        onLoad?.(e);
      }}
      className={`smart-img ${loaded ? "is-loaded" : ""} ${className}`}
      {...rest}
    />
  );
}
